import React, { useState, useEffect } from 'react';
import './PlayRecordModal.css';
import { getUILoc } from '../../utils/cardUtils.js';
import audioManager from '../../utils/AudioManager.js';
import {
  isPlayRecordEnabled,
  setPlayRecordEnabled,
  getPlayRecordLimit,
  setPlayRecordLimit,
  getPlayRecords,
  exportPlayRecordsAsJson,
  MIN_RECORD_LIMIT,
  MAX_RECORD_LIMIT,
} from '../../utils/playRecordUtils.js';

const PlayRecordModal = ({ isOpen, onClose }) => {
  const [enabled, setEnabled] = useState(false);
  const [limit, setLimit] = useState(MAX_RECORD_LIMIT);
  const [recordCount, setRecordCount] = useState(0);
  const [feedbackMessage, setFeedbackMessage] = useState('');

  useEffect(() => {
    if (isOpen) {
      setEnabled(isPlayRecordEnabled());
      setLimit(getPlayRecordLimit());
      setRecordCount(getPlayRecords().length);
      setFeedbackMessage('');
    }
  }, [isOpen]);

  if (!isOpen) {
    return null;
  }

  const handleToggleEnabled = (e) => {
    const val = e.target.checked;
    setEnabled(val);
    setPlayRecordEnabled(val);
    audioManager.playSE('select');
  };

  const handleLimitChange = (e) => {
    const rawVal = e.target.value;
    if (rawVal === '') {
      setLimit('');
      return;
    }
    const num = parseInt(rawVal, 10);
    if (!isNaN(num)) {
      const clamped = Math.max(MIN_RECORD_LIMIT, Math.min(MAX_RECORD_LIMIT, num));
      setLimit(clamped);
      setPlayRecordLimit(clamped);
      setRecordCount(getPlayRecords().length);
    }
  };

  const handleLimitBlur = () => {
    if (limit === '' || isNaN(parseInt(limit, 10))) {
      const defaultVal = MAX_RECORD_LIMIT;
      setLimit(defaultVal);
      setPlayRecordLimit(defaultVal);
      setRecordCount(getPlayRecords().length);
    }
  };

  const handleExport = () => {
    const res = exportPlayRecordsAsJson();
    if (res.success) {
      audioManager.playSE('select');
      setRecordCount(0);
      setFeedbackMessage(getUILoc('PLAY_RECORD.EXPORT_SUCCESS'));
    } else {
      audioManager.playSE('cancel');
      alert(getUILoc('PLAY_RECORD.NO_DATA_ALERT'));
    }
  };

  const handleClose = () => {
    audioManager.playSE('cancel');
    onClose();
  };

  return (
    <div className="play-record-modal-overlay" onClick={handleClose}>
      <div className="play-record-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="play-record-modal-header">
          <h3>{getUILoc('PLAY_RECORD.TITLE')}</h3>
          <button onClick={handleClose} className="play-record-modal-close-button">&times;</button>
        </div>

        <div className="play-record-modal-body">
          {/* 記録の有効/無効チェックボックス */}
          <div className="play-record-group toggle-group">
            <label htmlFor="play-record-enable-toggle" className="toggle-label">
              <span>{getUILoc('PLAY_RECORD.ENABLE_RECORDING')}</span>
            </label>
            <input
              id="play-record-enable-toggle"
              type="checkbox"
              checked={enabled}
              onChange={handleToggleEnabled}
            />
          </div>

          {/* 保存上限設定 (FIFO) */}
          <div className="play-record-group limit-group">
            <label htmlFor="play-record-limit-input">
              {getUILoc('PLAY_RECORD.RECORD_LIMIT')}
            </label>
            <div className="limit-input-wrapper">
              <input
                id="play-record-limit-input"
                type="number"
                min={MIN_RECORD_LIMIT}
                max={MAX_RECORD_LIMIT}
                value={limit}
                onChange={handleLimitChange}
                onBlur={handleLimitBlur}
              />
              <span className="limit-hint">({MIN_RECORD_LIMIT}〜{MAX_RECORD_LIMIT})</span>
            </div>
          </div>

          {/* 現在の保存件数 */}
          <div className="play-record-stats">
            <span className="stats-label">
              {getUILoc('PLAY_RECORD.CURRENT_COUNT', { count: recordCount, limit: limit || MAX_RECORD_LIMIT })}
            </span>
          </div>

          {/* 出力ボタン */}
          <div className="play-record-actions">
            <button className="play-record-export-btn" onClick={handleExport}>
              {getUILoc('PLAY_RECORD.EXPORT_BUTTON')}
            </button>
          </div>

          {/* フィードバックメッセージ */}
          {feedbackMessage && (
            <div className="play-record-feedback">
              {feedbackMessage}
            </div>
          )}
        </div>

        <div className="play-record-modal-footer">
          <button onClick={handleClose} className="play-record-close-button-bottom">
            {getUILoc('PLAY_RECORD.CLOSE')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PlayRecordModal;
